import { Button, CheckboxFormField, ClassName, Form, IFormRender, Theme } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import DashboardRibbon from 'components/foundation/dashboard/DashboardRibbon/DashboardRibbon';
import { useToastOpen } from 'contexts/ToastContext';
import { usePositionUpdate } from 'hooks';
import { t } from 'i18next';
import { isEqual } from 'lodash';
import { MutableRefObject, useRef } from 'react';

export interface ISafetyObservationsFormData {
  reportObservations: boolean;
  manageObservations: boolean;
}

export interface ISafetyObservationsFormProps {
  className?: ClassName;
  positionId: string;
  onSubmit: () => void;
  reportObservations: boolean;
  manageObservations: boolean;
}

const SafetyObservationsForm = (props: ISafetyObservationsFormProps) => {
  const { className, positionId, onSubmit, reportObservations, manageObservations } = props;

  const [, update] = usePositionUpdate();
  const toast = useToastOpen();
  const initialFormData: MutableRefObject<ISafetyObservationsFormData> = useRef({
    reportObservations,
    manageObservations,
  });

  const handleFormSubmit = async (formData: ISafetyObservationsFormData) => {
    if (!isEqual(initialFormData, formData)) {
      try {
        await update({
          id: positionId,
          reportObservations: formData.reportObservations,
          manageObservations: formData.reportObservations ? formData.manageObservations : false,
        });
      } catch (error) {
        toast({ label: t('Something went wrong. Please try again later.') });
      }
    }
    onSubmit();
  };

  const performLabel = () => (
    <>
      <h4>{t('Report Safety Observations')}</h4>
      <h6>
        {t('Allow the user to report safety concerns they may observe, as well as any positive safety observations.')}
      </h6>
    </>
  );

  const manageLabel = () => (
    <>
      <h4>{t('Manage Observations')}</h4>
      <h6>{t('Allow the user to view and process safety observations submitted by other employees.')}</h6>
    </>
  );

  const renderFormFields = (form: IFormRender<ISafetyObservationsFormData>) => (
    <div className={'m-5 pt-8'}>
      <CheckboxFormField name={'reportObservations'} form={form} label={performLabel()} />
      {form.state.data.reportObservations && (
        <DashboardRibbon theme={Theme.LIGHT}>
          <CheckboxFormField name={'manageObservations'} form={form} label={manageLabel()} />
        </DashboardRibbon>
      )}
      <Button onClick={form.handleSubmit} type="primary" className={'mt-4'}>
        {t('Update')}
      </Button>
    </div>
  );

  const classes = classNames('safety-observations-form', className);

  return (
    <Form render={renderFormFields} onSubmit={handleFormSubmit} data={initialFormData.current} className={classes} />
  );
};

export default SafetyObservationsForm;
